import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAllSeries } from "../utils/seriesUtils";
import { getAllMovies } from "../utils/movieUtils";
import { getAllBooks } from "../utils/bookUtils";
import { getAllComics } from "../utils/comicUtils";
import { useLang } from "../i18n/LanguageContext";
import { getCurrentUser } from "../utils/auth";
import { getHistory } from "../utils/watchHistory";
import { handleImgError } from "../utils/thumbnailUtils";
import { PageSEO } from "../hooks/useSEO";
import "../styles/styles.css";

/* ── Fila horizontal estilo Netflix ── */
const ContentRow = ({ title, items, type, seeAll }) => {
  const navigate = useNavigate();
  const rowRef = useRef(null);

  const scroll = (dir) => {
    if (rowRef.current) rowRef.current.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  const getPath = (item) => {
    if (type === "series") return `/series/${item.id}`;
    if (type === "movies") return `/movies/${item.id}`;
    if (type === "books") return `/books/${item.id}`;
    if (type === "comics") return `/comics/${item.id}`;
    return "/";
  };

  if (!items || items.length === 0) return null;

  return (
    <section className="content-row">
      <div className="row-header">
        <h2 className="row-title">{title}</h2>
        <button className="row-see-all" onClick={() => navigate(`/${type}`)}>
          {seeAll}
        </button>
      </div>
      <div className="row-scroll-wrapper">
        <button className="scroll-btn scroll-left" onClick={() => scroll(-1)}>‹</button>
        <div className="row-items" ref={rowRef}>
          {items.map((item) => (
            <div
              key={item.id}
              className="row-card"
              onClick={() => navigate(getPath(item))}
            >
              <div className="row-card-img-wrap">
                <img src={item.thumbnail} alt={item.title} loading="lazy" onError={(e) => handleImgError(e, item.title, 800, 450)} />
                {item.status && item.status !== "Completa" && (
                  <span className={`row-card-status row-card-status--${item.status === "Próximamente" ? "soon" : item.status === "En producción" ? "wip" : "highlight"}`}>
                    {item.status}
                  </span>
                )}
                <div className="row-card-hover">
                  <button className="btn-play">▶ Ver</button>
                  <p className="row-card-synopsis">
                    {item.synopsis?.slice(0, 80)}...
                  </p>
                </div>
              </div>
              <p className="row-card-title">{item.title}</p>
            </div>
          ))}
        </div>
        <button className="scroll-btn scroll-right" onClick={() => scroll(1)}>›</button>
      </div>
    </section>
  );
};

/* ── Hero con vídeo estilo Netflix ── */
const VideoHero = ({ heroItems, heroIdx, setHeroIdx, series, navigate, tr }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(false);

  const hero = heroItems[heroIdx];

  useEffect(() => {
    setVideoLoaded(false);
    if (videoRef.current && hero?.trailerUrl) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [hero?.trailerUrl]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  const toggleMute = useCallback((e) => {
    e.stopPropagation();
    setMuted((m) => !m);
  }, []);

  if (!hero) return null;

  const isSeries = series.some((s) => s.id === hero.id);
  const heroPath = isSeries ? `/series/${hero.id}` : `/movies/${hero.id}`;
  const typeLabel = isSeries ? tr.hero.series : tr.hero.movie;

  return (
    <div className="hero-banner hero-video-wrapper">
      {hero.trailerUrl ? (
        <>
          <video
            ref={videoRef}
            className={`hero-video ${videoLoaded ? "hero-video--visible" : ""}`}
            src={hero.trailerUrl}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onCanPlay={() => setVideoLoaded(true)}
          />
          <div
            className={`hero-video-poster ${videoLoaded ? "hero-video-poster--hidden" : ""}`}
            style={{ backgroundImage: `url(${hero.thumbnail})` }}
          />
        </>
      ) : (
        <div
          className="hero-video-poster"
          style={{ backgroundImage: `url(${hero.thumbnail})` }}
        />
      )}

      <div className="hero-overlay" />
      <div className="hero-overlay-bottom" />

      <div className="hero-content">
        <span className="hero-badge">{typeLabel} · {tr.hero.original}</span>
        <h1 className="hero-title">{hero.title}</h1>
        <p className="hero-synopsis">{hero.synopsis?.slice(0, 160)}...</p>
        <div className="hero-actions">
          <button className="btn-primary" onClick={() => navigate(heroPath)}>
            {tr.hero.watchNow}
          </button>
          <button className="btn-secondary" onClick={() => navigate(heroPath)}>
            {tr.hero.myList}
          </button>
        </div>
      </div>

      <div className="hero-controls">
        {hero.trailerUrl && (
          <button className="hero-mute-btn" onClick={toggleMute}>
            {muted ? "🔇" : "🔊"}
          </button>
        )}
        <div className="hero-dots">
          {heroItems.map((_, i) => (
            <span
              key={i}
              className={`hero-dot ${i === heroIdx ? "active" : ""}`}
              onClick={() => setHeroIdx(i)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Home principal ── */
const Home = () => {
  const navigate = useNavigate();
  const { tr } = useLang();
  const [series, setSeries]   = useState([]);
  const [movies, setMovies]   = useState([]);
  const [books, setBooks]     = useState([]);
  const [comics, setComics]   = useState([]);
  const [history, setHistory] = useState([]);
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    setSeries(getAllSeries());
    setMovies(getAllMovies());
    setBooks(getAllBooks());
    setComics(getAllComics());
    const u = getCurrentUser();
    if (u) setHistory(getHistory(u.email));
  }, []);

  const heroItems = [...series, ...movies]
    .sort((a, b) => b.popularity - a.popularity)
    .slice(0, 5);

  useEffect(() => {
    if (heroItems.length === 0) return;
    const interval = setInterval(() => {
      setHeroIdx((i) => (i + 1) % heroItems.length);
    }, 12000);
    return () => clearInterval(interval);
  }, [heroItems.length]);

  return (
    <div className="home-container">
      <PageSEO
        title="AI Cinema Platform — Series, Películas y Libros IA"
        description="Descubre series, películas, libros y cómics originales creados con inteligencia artificial. Solo en AIVEO — el primer cine generado por IA."
        url="/"
      />
      <VideoHero
        heroItems={heroItems}
        heroIdx={heroIdx}
        setHeroIdx={setHeroIdx}
        series={series}
        navigate={navigate}
        tr={tr}
      />

      {/* ── FRANJA DE STATS ── */}
      <div className="home-stats-bar">
        <div className="home-stat"><span className="home-stat-num">15+</span><span className="home-stat-label">Series IA</span></div>
        <div className="home-stat-divider" />
        <div className="home-stat"><span className="home-stat-num">12+</span><span className="home-stat-label">Películas IA</span></div>
        <div className="home-stat-divider" />
        <div className="home-stat"><span className="home-stat-num">5</span><span className="home-stat-label">Idiomas</span></div>
        <div className="home-stat-divider" />
        <div className="home-stat"><span className="home-stat-num">100%</span><span className="home-stat-label">Generado con IA</span></div>
      </div>

      {/* ── FILAS DE CONTENIDO ── */}
      {history.length > 0 && (
        <ContentRow title="Continuar viendo" items={history.map(h => [...series, ...movies].find(x => x.id === h.id)).filter(Boolean)} type={history[0]?.type || "series"} seeAll="Ver todo" />
      )}
      <ContentRow title="Series destacadas" items={series.slice(0, 10)} type="series" seeAll="Ver todas" />
      <ContentRow title="Películas IA" items={movies.slice(0, 10)} type="movies" seeAll="Ver todas" />
      <ContentRow title="Libros generativos" items={books.slice(0, 6)} type="books" seeAll="Ver todos" />
      <ContentRow title="Cómics IA" items={comics.slice(0, 6)} type="comics" seeAll="Ver todos" />

      {/* ── QUÉ ES AIVEO ── */}
      <div className="home-value-section">
        <h2 className="home-value-title">
          El primer estudio de cine<br />
          <span className="home-gradient-text">creado por inteligencia artificial</span>
        </h2>
        <p className="home-value-lead">
          AIVEO no adapta ni copia — <strong>origina</strong>. Cada serie, película, libro y cómic
          es generado desde cero con IA, con guiones, visuales y voces 100% artificiales.
          No es el futuro del entretenimiento. Es el presente.
        </p>
        <div className="home-value-cards">
          <div className="home-value-card">
            <div className="home-value-icon">🎬</div>
            <h3>Contenido original</h3>
            <p>Series y películas con tramas, personajes y mundos generados íntegramente por IA. Nada de remakes. Todo nuevo.</p>
          </div>
          <div className="home-value-card">
            <div className="home-value-icon">📚</div>
            <h3>Biblioteca IA</h3>
            <p>Libros y cómics que ningún humano ha escrito o dibujado. Literatura e imagen visual generativas al 100%.</p>
          </div>
          <div className="home-value-card">
               <div className="home-value-icon">🤖</div>
            <h3>Solo en AIVEO</h3>
            <p>No encontrarás estas historias en ningún otro lugar. Son propias, únicas y 100% artificiales.</p>
          </div>
        </div>
      </div>

      {/* ── TRUST SIGNALS ── */}
      <div className="home-trust-bar">
        <div className="home-trust-item">
          <span className="home-trust-icon">🤖</span>
          <span className="home-trust-value">100% IA</span>
          <span className="home-trust-label">Todo generado con IA</span>
        </div>
        <div className="home-trust-item">
          <span className="home-trust-icon">🆓</span>
          <span className="home-trust-value">Beta gratuita</span>
          <span className="home-trust-label">Sin tarjeta requerida</span>
        </div>
        <div className="home-trust-item">
          <span className="home-trust-icon">🌍</span>
          <span className="home-trust-value">5 idiomas</span>
          <span className="home-trust-label">ES · EN · PT · FR · DE</span>
        </div>
        <div className="home-trust-item">
          <span className="home-trust-icon">🚫</span>
          <span className="home-trust-value">Sin publicidad</span>
          <span className="home-trust-label">Experiencia limpia</span>
        </div>
        <div className="home-trust-item">
          <span className="home-trust-icon">🎬</span>
          <span className="home-trust-value">27+ títulos</span>
          <span className="home-trust-label">Series, películas, libros</span>
        </div>
      </div>

    </div>
  );
};

export default Home;
